import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, CircularProgress, IconButton, Modal, Tooltip, Typography } from "@mui/material";
import ChatBubbleRoundedIcon from "@mui/icons-material/ChatBubbleRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import { isDarkMode, themeColors } from "../utils/constants";

const QR_VERSION = 5;
const QR_SIZE = 21 + (QR_VERSION - 1) * 4;
const QR_DATA_CODEWORDS = 108;
const QR_ECC_CODEWORDS = 26;
const QR_REMAINDER_BITS = 7;
const QR_ECL_BITS = 1; // Low error correction.

const postJson = async (url, body = {}) => {
  const response = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data?.error || "Request failed");
  return data;
};

const copyValue = async (value) => {
  if (!value) return;
  await navigator.clipboard.writeText(value);
};

const cleanPhoneNumber = (value) => String(value || "").trim().replace(/^['"]+|['"]+$/g, "").trim();

const formatPhoneNumber = (value) => {
  const clean = cleanPhoneNumber(value);
  const match = clean.match(/^\+1(\d{3})(\d{3})(\d{4})$/);
  if (!match) return clean;
  return `+1 (${match[1]}) ${match[2]}-${match[3]}`;
};

const smsLinkFor = (number, message) => {
  if (!number || !message) return null;
  return `sms:${number}?body=${encodeURIComponent(message)}`;
};

const sanitizeSmsLink = (value) => {
  const raw = String(value || "").trim();
  if (!raw.toLowerCase().startsWith("sms:")) return raw;
  return raw.replace(/^sms:([^?]*)/i, (_match, number) => `sms:${cleanPhoneNumber(number)}`);
};

const TextMessagingSetupModal = ({
  open,
  onClose,
  context = {},
  source = "text_messaging_setup_modal",
  autoCreate = true,
}) => {
  const [status, setStatus] = useState(null);
  const [pairing, setPairing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(null);
  const [pairingWaitTimedOut, setPairingWaitTimedOut] = useState(false);
  const dismissedRef = useRef(false);

  const requestBody = useMemo(() => ({
    source: context?.source || source,
    surface: context?.surface || "chat",
    agent_task_id: context?.agentTaskId || context?.agent_task_id,
    chat_message_id: context?.chatMessageId || context?.chat_message_id,
  }), [context, source]);

  const loadStatus = useCallback(async ({ background = false } = {}) => {
    if (!background) {
      setLoading(true);
      setError(null);
    }
    try {
      const response = await fetch("/text_messaging/status");
      const data = await response.json();
      if (!response.ok) throw new Error(data?.error || "Failed to load texting status");
      setStatus(data);
    } catch (e) {
      if (!background) setError(e.message || "Failed to load texting status");
    } finally {
      if (!background) setLoading(false);
    }
  }, []);

  const createPairingToken = useCallback(async () => {
    if (saving) return;
    setSaving(true);
    setError(null);
    try {
      const data = await postJson("/text_messaging/pairing_tokens", requestBody);
      setStatus(data);
      setPairing(data.pairing);
    } catch (e) {
      setError(e.message || "Failed to create text setup message");
    } finally {
      setSaving(false);
    }
  }, [requestBody, saving]);

  useEffect(() => {
    if (!open) return;

    dismissedRef.current = false;
    setPairing(null);
    loadStatus();
  }, [open, loadStatus]);

  useEffect(() => {
    if (!open || !autoCreate || pairing || saving || loading || error) return;
    if (status && !status.identity && !status.setup_error) {
      createPairingToken();
    }
  }, [open, autoCreate, status, pairing, saving, loading, error, createPairingToken]);

  useEffect(() => {
    if (!open || !pairing || status?.identity) return undefined;
    const pollId = window.setInterval(() => loadStatus({ background: true }), 2000);
    return () => window.clearInterval(pollId);
  }, [open, pairing, status?.identity, loadStatus]);

  useEffect(() => {
    if (!open || !pairing || status?.identity) {
      setPairingWaitTimedOut(false);
      return undefined;
    }

    setPairingWaitTimedOut(false);
    const timeoutId = window.setTimeout(() => setPairingWaitTimedOut(true), 55000);
    return () => window.clearTimeout(timeoutId);
  }, [open, pairing, status?.identity]);

  useEffect(() => {
    if (!open || !pairing || !status?.identity) return undefined;
    const timeoutId = window.setTimeout(() => onClose?.(), 1300);
    return () => window.clearTimeout(timeoutId);
  }, [open, pairing, status?.identity, onClose]);

  const handleCopy = async (label, value) => {
    try {
      await copyValue(value);
      setCopied(label);
      window.setTimeout(() => setCopied(null), 1600);
    } catch (e) {
      setError("Copy failed");
    }
  };

  const handleCopyKeyDown = (event, label, value) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    handleCopy(label, value);
  };

  const handleCopyIconClick = (event, label, value) => {
    event.stopPropagation();
    handleCopy(label, value);
  };

  const dismissPreference = async () => {
    if (dismissedRef.current || status?.identity) return;
    dismissedRef.current = true;
    try {
      await postJson("/text_messaging/preferences/dismiss", requestBody);
    } catch (_e) {
      return null;
    }
  };

  const declinePreference = async () => {
    try {
      await postJson("/text_messaging/preferences/decline", requestBody);
    } catch (_e) {
      return null;
    } finally {
      onClose?.();
    }
  };

  const closeModal = async () => {
    await dismissPreference();
    onClose?.();
  };

  const sendblueNumber = cleanPhoneNumber(pairing?.sendblue_number || status?.sendblue_number);
  const displaySendblueNumber = formatPhoneNumber(sendblueNumber);
  const setupSmsLink = smsLinkFor(sendblueNumber, pairing?.message) || sanitizeSmsLink(pairing?.sms_link);
  const qrValue = setupSmsLink || sanitizeSmsLink(pairing?.qr_value || pairing?.message);
  const isConnected = !!status?.identity;
  const isPairingConfirmed = !!pairing && isConnected;
  const showConnectedSummary = isConnected && !pairing;
  const isReady = !!pairing;
  const pairingExpiresText = useMemo(() => {
    if (!pairing?.expires_at) return null;

    const expiresAt = Date.parse(pairing.expires_at);
    if (Number.isNaN(expiresAt)) return null;

    const minutes = Math.max(1, Math.round((expiresAt - Date.now()) / 60000));
    return `Pairing code expires in ${minutes} minute${minutes === 1 ? "" : "s"}.`;
  }, [pairing?.expires_at]);

  const copySetupMessageAndOpenMessages = async () => {
    if (!pairing?.message || !setupSmsLink) return;
    try {
      await copyValue(pairing.message);
      setCopied("openMessages");
      window.setTimeout(() => setCopied(null), 1600);
    } catch (e) {
      setError("Message was not copied. Use Copy Message before sending.");
    } finally {
      window.location.href = setupSmsLink;
    }
  };

  return (
    <Modal open={!!open} onClose={closeModal}>
      <Box sx={modalStyles}>
        {showConnectedSummary ? (
          <Box sx={connectedTitleStyles}>
            <CheckRoundedIcon sx={connectedTitleIconStyles} />
            <Typography sx={titleStyles}>Phone connected</Typography>
          </Box>
        ) : (
          <Typography sx={titleStyles}>Get Incite alerts by text</Typography>
        )}

        {loading || (saving && !pairing) ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : status?.setup_error ? (
          <Typography sx={errorStyles}>
            Texting number is not loaded. Confirm SENDBLUE_DEFAULT_FROM_NUMBER and restart the server.
          </Typography>
        ) : showConnectedSummary ? (
          <>
            <Typography sx={mutedTextStyles}>
              You&apos;ll receive text alerts for triggered agent tasks on this number.
            </Typography>
            <Button variant="contained" onClick={onClose} sx={primaryButtonStyles}>
              Done
            </Button>
          </>
        ) : (
          <>
            <Typography sx={subtitleStyles}>
              Receive price triggers, agent-task updates, and follow-up analysis by text.
            </Typography>

            {isReady ? (
              <Box sx={setupGridStyles}>
                <Box sx={setupColumnStyles}>
                  <Box sx={sectionStyles}>
                    <Typography sx={labelStyles}>Text this number</Typography>
                    <Box
                      sx={numberStyles}
                      role="button"
                      tabIndex={0}
                      aria-label="Copy text number"
                      onClick={() => handleCopy("number", sendblueNumber)}
                      onKeyDown={(event) => handleCopyKeyDown(event, "number", sendblueNumber)}
                    >
                      <Typography component="span" sx={phoneNumberTextStyles}>
                        {displaySendblueNumber}
                      </Typography>
                      <Tooltip title={copied === "number" ? "Copied" : "Copy number"}>
                        <IconButton
                          size="small"
                          aria-label="Copy text number"
                          disabled={!sendblueNumber}
                          onClick={(event) => handleCopyIconClick(event, "number", sendblueNumber)}
                          sx={copyIconButtonStyles}
                        >
                          {copied === "number" ? (
                            <CheckRoundedIcon sx={{ fontSize: 20 }} />
                          ) : (
                            <ContentCopyRoundedIcon sx={{ fontSize: 20 }} />
                          )}
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>

                  <Box sx={sectionStyles}>
                    <Typography sx={labelStyles}>Send this setup text</Typography>
                    <Box
                      sx={codeBlockStyles}
                      role="button"
                      tabIndex={0}
                      aria-label="Copy setup text"
                      onClick={() => handleCopy("setupText", pairing.message)}
                      onKeyDown={(event) => handleCopyKeyDown(event, "setupText", pairing.message)}
                    >
                      <Typography component="span" sx={codeTextStyles}>
                        {pairing.message}
                      </Typography>
                      <Tooltip title={copied === "setupText" ? "Copied" : "Copy setup text"}>
                        <IconButton
                          size="small"
                          aria-label="Copy setup text"
                          disabled={!pairing.message}
                          onClick={(event) => handleCopyIconClick(event, "setupText", pairing.message)}
                          sx={copyIconButtonStyles}
                        >
                          {copied === "setupText" ? (
                            <CheckRoundedIcon sx={{ fontSize: 20 }} />
                          ) : (
                            <ContentCopyRoundedIcon sx={{ fontSize: 20 }} />
                          )}
                        </IconButton>
                      </Tooltip>
                    </Box>
                    {pairingExpiresText && (
                      <Typography sx={expiryTextStyles}>{pairingExpiresText}</Typography>
                    )}
                    <Box sx={pairingStatusStyles(isPairingConfirmed)} aria-live="polite">
                      {isPairingConfirmed ? (
                        <CheckRoundedIcon sx={pairingStatusCheckStyles} />
                      ) : !pairingWaitTimedOut && (
                        <CircularProgress size={12} thickness={5} sx={waitingSpinnerStyles} />
                      )}
                      <Typography component="span" sx={waitingStatusTextStyles}>
                        {isPairingConfirmed
                          ? "Phone connected"
                          : pairingWaitTimedOut
                          ? "Still waiting? Make sure the text was sent, or copy the setup text again."
                          : "Waiting for your text…"}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={ctaGroupStyles}>
                    <Button
                      variant="contained"
                      disabled={!setupSmsLink || !pairing.message}
                      onClick={copySetupMessageAndOpenMessages}
                      startIcon={<ChatBubbleRoundedIcon sx={{ fontSize: 18 }} />}
                      sx={primaryCtaButtonStyles}
                    >
                      Open in Messages
                    </Button>
                  </Box>
                </Box>

                <Box sx={qrPanelStyles}>
                  <Typography sx={qrHeadingStyles}>Or scan to pair your phone</Typography>
                  <QrCode value={qrValue} />
                  <Typography sx={{ ...mutedTextStyles, fontSize: "0.76rem", mt: 1, mb: 0 }}>
                    Opens Messages with setup text prefilled.
                  </Typography>
                </Box>
              </Box>
            ) : (
              <Button variant="contained" disabled={saving} onClick={createPairingToken} sx={primaryButtonStyles}>
                {saving ? "Preparing..." : "Set Up Texting"}
              </Button>
            )}
          </>
        )}

        {error && <Typography sx={errorStyles}>{error}</Typography>}

        {!isConnected && !loading && (
          <Box sx={footerStyles}>
            <Button variant="text" onClick={declinePreference} sx={textButtonStyles}>
              Continue without text alerts
            </Button>
          </Box>
        )}
      </Box>
    </Modal>
  );
};

const QrCode = ({ value }) => {
  const path = buildQrPath(value || "");
  const quietZone = 4;
  const size = QR_SIZE + quietZone * 2;

  return (
    <Box sx={qrStyles}>
      <svg
        width="176"
        height="176"
        viewBox={`0 0 ${size} ${size}`}
        role="img"
        aria-label="Text setup QR code"
        shapeRendering="crispEdges"
      >
        <rect width={size} height={size} fill="#fff" />
        <path d={path} fill="#101828" transform={`translate(${quietZone} ${quietZone})`} />
      </svg>
    </Box>
  );
};

const buildQrPath = (value) => {
  const matrix = makeQrMatrix(value);
  const segments = [];
  for (let y = 0; y < matrix.length; y += 1) {
    let x = 0;
    while (x < matrix.length) {
      if (!matrix[y][x]) {
        x += 1;
        continue;
      }
      const start = x;
      while (x < matrix.length && matrix[y][x]) x += 1;
      segments.push(`M${start} ${y}h${x - start}v1H${start}z`);
    }
  }
  return segments.join("");
};

const makeQrMatrix = (value) => {
  const dataCodewords = encodeQrData(value);
  const eccCodewords = reedSolomonRemainder(dataCodewords, QR_ECC_CODEWORDS);
  const bits = [];
  [...dataCodewords, ...eccCodewords].forEach((codeword) => {
    for (let i = 7; i >= 0; i -= 1) bits.push(((codeword >>> i) & 1) === 1);
  });
  for (let i = 0; i < QR_REMAINDER_BITS; i += 1) bits.push(false);

  let best = null;
  for (let mask = 0; mask < 8; mask += 1) {
    const matrix = blankMatrix();
    drawFunctionPatterns(matrix);
    drawCodewords(matrix, bits);
    applyMask(matrix, mask);
    drawFormatBits(matrix, mask);
    const penalty = scorePenalty(matrix.modules);
    if (!best || penalty < best.penalty) best = { matrix: matrix.modules, penalty };
  }
  return best.matrix;
};

const blankMatrix = () => ({
  modules: Array.from({ length: QR_SIZE }, () => Array(QR_SIZE).fill(false)),
  functionModules: Array.from({ length: QR_SIZE }, () => Array(QR_SIZE).fill(false)),
});

const setFunction = (matrix, x, y, dark) => {
  if (x < 0 || y < 0 || x >= QR_SIZE || y >= QR_SIZE) return;
  matrix.modules[y][x] = !!dark;
  matrix.functionModules[y][x] = true;
};

const drawFunctionPatterns = (matrix) => {
  drawFinder(matrix, 3, 3);
  drawFinder(matrix, QR_SIZE - 4, 3);
  drawFinder(matrix, 3, QR_SIZE - 4);
  for (let i = 0; i < QR_SIZE; i += 1) {
    if (!matrix.functionModules[6][i]) setFunction(matrix, i, 6, i % 2 === 0);
    if (!matrix.functionModules[i][6]) setFunction(matrix, 6, i, i % 2 === 0);
  }
  drawAlignment(matrix, 30, 30);
  reserveFormatAreas(matrix);
  setFunction(matrix, 8, QR_SIZE - 8, true);
};

const drawFinder = (matrix, cx, cy) => {
  for (let dy = -4; dy <= 4; dy += 1) {
    for (let dx = -4; dx <= 4; dx += 1) {
      const distance = Math.max(Math.abs(dx), Math.abs(dy));
      const dark = distance !== 4 && (distance === 3 || distance <= 1);
      setFunction(matrix, cx + dx, cy + dy, dark);
    }
  }
};

const drawAlignment = (matrix, cx, cy) => {
  for (let dy = -2; dy <= 2; dy += 1) {
    for (let dx = -2; dx <= 2; dx += 1) {
      const distance = Math.max(Math.abs(dx), Math.abs(dy));
      setFunction(matrix, cx + dx, cy + dy, distance === 2 || distance === 0);
    }
  }
};

const reserveFormatAreas = (matrix) => {
  for (let i = 0; i < 9; i += 1) {
    if (i !== 6) {
      setFunction(matrix, 8, i, false);
      setFunction(matrix, i, 8, false);
    }
  }
  for (let i = 0; i < 8; i += 1) {
    setFunction(matrix, QR_SIZE - 1 - i, 8, false);
    setFunction(matrix, 8, QR_SIZE - 1 - i, false);
  }
};

const drawCodewords = (matrix, bits) => {
  let bitIndex = 0;
  let upward = true;
  for (let right = QR_SIZE - 1; right >= 1; right -= 2) {
    if (right === 6) right -= 1;
    for (let vertical = 0; vertical < QR_SIZE; vertical += 1) {
      const y = upward ? QR_SIZE - 1 - vertical : vertical;
      for (let j = 0; j < 2; j += 1) {
        const x = right - j;
        if (matrix.functionModules[y][x]) continue;
        matrix.modules[y][x] = bits[bitIndex] || false;
        bitIndex += 1;
      }
    }
    upward = !upward;
  }
};

const applyMask = (matrix, mask) => {
  for (let y = 0; y < QR_SIZE; y += 1) {
    for (let x = 0; x < QR_SIZE; x += 1) {
      if (!matrix.functionModules[y][x] && maskCondition(mask, x, y)) {
        matrix.modules[y][x] = !matrix.modules[y][x];
      }
    }
  }
};

const maskCondition = (mask, x, y) => {
  switch (mask) {
    case 0: return (x + y) % 2 === 0;
    case 1: return y % 2 === 0;
    case 2: return x % 3 === 0;
    case 3: return (x + y) % 3 === 0;
    case 4: return (Math.floor(y / 2) + Math.floor(x / 3)) % 2 === 0;
    case 5: return ((x * y) % 2) + ((x * y) % 3) === 0;
    case 6: return (((x * y) % 2) + ((x * y) % 3)) % 2 === 0;
    case 7: return (((x + y) % 2) + ((x * y) % 3)) % 2 === 0;
    default: return false;
  }
};

const drawFormatBits = (matrix, mask) => {
  const bits = formatBits(mask);
  for (let i = 0; i <= 5; i += 1) setFunction(matrix, 8, i, bit(bits, i));
  setFunction(matrix, 8, 7, bit(bits, 6));
  setFunction(matrix, 8, 8, bit(bits, 7));
  setFunction(matrix, 7, 8, bit(bits, 8));
  for (let i = 9; i < 15; i += 1) setFunction(matrix, 14 - i, 8, bit(bits, i));
  for (let i = 0; i < 8; i += 1) setFunction(matrix, QR_SIZE - 1 - i, 8, bit(bits, i));
  for (let i = 8; i < 15; i += 1) setFunction(matrix, 8, QR_SIZE - 15 + i, bit(bits, i));
  setFunction(matrix, 8, QR_SIZE - 8, true);
};

const formatBits = (mask) => {
  const data = (QR_ECL_BITS << 3) | mask;
  let remainder = data;
  for (let i = 0; i < 10; i += 1) {
    remainder = (remainder << 1) ^ (((remainder >>> 9) & 1) * 0x537);
  }
  return ((data << 10) | remainder) ^ 0x5412;
};

const bit = (value, index) => ((value >>> index) & 1) === 1;

const encodeQrData = (value) => {
  const bytes = new TextEncoder().encode(value || "INCITE");
  if (bytes.length > 100) throw new Error("Text setup QR value is too long");

  const bits = [];
  appendBits(bits, 0x4, 4);
  appendBits(bits, bytes.length, 8);
  bytes.forEach((byteValue) => appendBits(bits, byteValue, 8));

  const capacityBits = QR_DATA_CODEWORDS * 8;
  appendBits(bits, 0, Math.min(4, capacityBits - bits.length));
  while (bits.length % 8 !== 0) bits.push(false);

  const codewords = [];
  for (let i = 0; i < bits.length; i += 8) {
    let codeword = 0;
    for (let j = 0; j < 8; j += 1) codeword = (codeword << 1) | (bits[i + j] ? 1 : 0);
    codewords.push(codeword);
  }
  for (let pad = 0xec; codewords.length < QR_DATA_CODEWORDS; pad ^= 0xfd) {
    codewords.push(pad);
  }
  return codewords;
};

const appendBits = (target, value, length) => {
  for (let i = length - 1; i >= 0; i -= 1) target.push(((value >>> i) & 1) === 1);
};

const reedSolomonRemainder = (data, degree) => {
  const generator = reedSolomonGenerator(degree);
  const result = Array(degree).fill(0);
  data.forEach((dataByte) => {
    const factor = dataByte ^ result.shift();
    result.push(0);
    for (let i = 0; i < degree; i += 1) {
      result[i] ^= gfMultiply(generator[i], factor);
    }
  });
  return result;
};

const reedSolomonGenerator = (degree) => {
  let result = [1];
  for (let i = 0; i < degree; i += 1) {
    const next = Array(result.length + 1).fill(0);
    result.forEach((coefficient, index) => {
      next[index] ^= coefficient;
      next[index + 1] ^= gfMultiply(coefficient, gfExp(i));
    });
    result = next;
  }
  return result.slice(1);
};

const gfExp = (power) => {
  let value = 1;
  for (let i = 0; i < power; i += 1) {
    value <<= 1;
    if (value & 0x100) value ^= 0x11d;
  }
  return value;
};

const gfMultiply = (left, right) => {
  let result = 0;
  let a = left;
  let b = right;
  while (b !== 0) {
    if (b & 1) result ^= a;
    a <<= 1;
    if (a & 0x100) a ^= 0x11d;
    b >>>= 1;
  }
  return result;
};

const scorePenalty = (matrix) => {
  let penalty = 0;
  for (let y = 0; y < QR_SIZE; y += 1) penalty += runPenalty(matrix[y]);
  for (let x = 0; x < QR_SIZE; x += 1) {
    const column = [];
    for (let y = 0; y < QR_SIZE; y += 1) column.push(matrix[y][x]);
    penalty += runPenalty(column);
  }
  for (let y = 0; y < QR_SIZE - 1; y += 1) {
    for (let x = 0; x < QR_SIZE - 1; x += 1) {
      const color = matrix[y][x];
      if (matrix[y][x + 1] === color && matrix[y + 1][x] === color && matrix[y + 1][x + 1] === color) {
        penalty += 3;
      }
    }
  }
  const dark = matrix.flat().filter(Boolean).length;
  const percent = (dark * 100) / (QR_SIZE * QR_SIZE);
  penalty += Math.floor(Math.abs(percent - 50) / 5) * 10;
  return penalty;
};

const runPenalty = (line) => {
  let penalty = 0;
  let current = line[0];
  let runLength = 1;
  for (let i = 1; i < line.length; i += 1) {
    if (line[i] === current) {
      runLength += 1;
    } else {
      if (runLength >= 5) penalty += runLength - 2;
      current = line[i];
      runLength = 1;
    }
  }
  if (runLength >= 5) penalty += runLength - 2;
  return penalty;
};

const modalStyles = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "min(92vw, 620px)",
  maxHeight: "88vh",
  overflowY: "auto",
  bgcolor: isDarkMode ? "#181818" : "#fff",
  border: `1px solid ${isDarkMode ? "rgba(255,255,255,0.12)" : "rgba(15,23,42,0.12)"}`,
  borderRadius: "8px",
  boxShadow: 24,
  p: 3,
};

const titleStyles = {
  color: themeColors.color,
  fontWeight: 720,
  fontSize: "1.12rem",
  lineHeight: 1.25,
};

const connectedTitleStyles = {
  display: "flex",
  alignItems: "center",
  gap: 0.75,
};

const connectedTitleIconStyles = {
  color: themeColors.linkColor,
  fontSize: 19,
};

const subtitleStyles = {
  color: themeColors.color,
  opacity: 0.74,
  fontSize: "0.86rem",
  lineHeight: 1.5,
  mt: 1,
  mb: 2.4,
  maxWidth: "500px",
};

const setupGridStyles = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", sm: "minmax(0, 1fr) 196px" },
  gap: { xs: "18px", sm: "24px" },
  alignItems: "start",
};

const setupColumnStyles = {
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  gap: 2,
};

const sectionStyles = {
  minWidth: 0,
};

const labelStyles = {
  color: themeColors.color,
  opacity: 0.62,
  fontSize: "0.72rem",
  fontWeight: 650,
  mb: 0.4,
};

const mutedTextStyles = {
  color: themeColors.color,
  opacity: 0.72,
  fontSize: "0.84rem",
  lineHeight: 1.5,
  my: 1.2,
};

const errorStyles = {
  color: "rgb(255,80,80)",
  fontSize: "0.78rem",
  mt: 1,
};

const numberStyles = {
  color: themeColors.color,
  background: isDarkMode ? "rgba(255,255,255,0.045)" : "rgba(15,23,42,0.035)",
  border: `1px solid ${isDarkMode ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.08)"}`,
  borderRadius: "6px",
  minHeight: "44px",
  px: 1.25,
  py: 0.85,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 1,
  cursor: "pointer",
  transition: "background 120ms ease, border-color 120ms ease",
  "&:hover": {
    background: isDarkMode ? "rgba(255,255,255,0.075)" : "rgba(15,23,42,0.055)",
    borderColor: isDarkMode ? "rgba(255,255,255,0.16)" : "rgba(15,23,42,0.14)",
  },
  "&:focus-visible": {
    outline: `2px solid ${themeColors.buttonPrimaryColor}`,
    outlineOffset: "2px",
  },
};

const phoneNumberTextStyles = {
  color: themeColors.color,
  fontWeight: 650,
  fontSize: "1rem",
  letterSpacing: 0,
  wordBreak: "break-word",
};

const codeBlockStyles = {
  color: themeColors.color,
  background: isDarkMode ? "rgba(255,255,255,0.055)" : "rgba(15,23,42,0.04)",
  border: `1px solid ${isDarkMode ? "rgba(255,255,255,0.12)" : "rgba(15,23,42,0.1)"}`,
  borderRadius: "6px",
  minHeight: "44px",
  px: 1.25,
  py: 0.85,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 1,
  cursor: "pointer",
  transition: "background 120ms ease, border-color 120ms ease",
  "&:hover": {
    background: isDarkMode ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.06)",
    borderColor: isDarkMode ? "rgba(255,255,255,0.2)" : "rgba(15,23,42,0.16)",
  },
  "&:focus-visible": {
    outline: `2px solid ${themeColors.buttonPrimaryColor}`,
    outlineOffset: "2px",
  },
};

const codeTextStyles = {
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
  fontSize: "0.88rem",
  fontWeight: 650,
  letterSpacing: 0,
  wordBreak: "break-word",
};

const copyIconButtonStyles = {
  flex: "0 0 auto",
  color: themeColors.color,
  opacity: 0.76,
  width: 34,
  height: 34,
  "&:hover": {
    background: isDarkMode ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.06)",
    opacity: 1,
  },
};

const expiryTextStyles = {
  color: themeColors.color,
  opacity: 0.55,
  fontSize: "0.72rem",
  mt: 0.7,
};

const pairingStatusStyles = (connected = false) => ({
  display: "flex",
  alignItems: "center",
  gap: 0.75,
  color: connected ? themeColors.linkColor : themeColors.color,
  opacity: connected ? 0.9 : 0.76,
  mt: 1.35,
});

const waitingSpinnerStyles = {
  color: themeColors.linkColor,
  opacity: 0.78,
};

const waitingStatusTextStyles = {
  fontSize: "0.72rem",
  lineHeight: 1.35,
};

const pairingStatusCheckStyles = {
  color: themeColors.linkColor,
  fontSize: 15,
  flex: "0 0 auto",
};

const ctaGroupStyles = {
  display: "flex",
  flexDirection: "column",
  alignItems: { xs: "stretch", sm: "flex-start" },
  mt: 0.1,
};

const qrPanelStyles = {
  display: { xs: "none", sm: "flex" },
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "flex-start",
  minWidth: 0,
  pt: 0.1,
};

const qrHeadingStyles = {
  color: themeColors.color,
  fontWeight: 650,
  fontSize: "0.82rem",
  mb: 1,
};

const qrStyles = {
  width: "176px",
  height: "176px",
  borderRadius: "6px",
  border: "1px solid rgba(15,23,42,0.12)",
  overflow: "hidden",
};

const primaryButtonStyles = {
  textTransform: "none",
  borderRadius: "7px",
  background: themeColors.buttonPrimaryColor,
  color: themeColors.primaryText,
  minHeight: "44px",
  px: 2.35,
  fontWeight: 700,
  border: isDarkMode ? "1px solid rgba(255,255,255,0.14)" : "1px solid rgba(15,23,42,0.08)",
  boxShadow: isDarkMode ? "0 11px 22px rgba(0,0,0,0.24)" : "0 9px 20px rgba(15,23,42,0.13)",
  width: { xs: "100%", sm: "auto" },
  "&:hover": { background: themeColors.buttonPrimaryColor, opacity: 0.9 },
};

const primaryCtaButtonStyles = {
  ...primaryButtonStyles,
  background: isDarkMode ? "rgba(46,127,241,0.74)" : "rgba(46,127,241,0.86)",
  color: "#fff",
  minHeight: "40px",
  px: 2.05,
  border: `1px solid ${isDarkMode ? "rgba(140,185,255,0.34)" : "rgba(46,127,241,0.36)"}`,
  boxShadow: isDarkMode ? "0 10px 20px rgba(46,127,241,0.14)" : "0 9px 18px rgba(46,127,241,0.12)",
  "&:hover": {
    background: isDarkMode ? "rgba(46,127,241,0.82)" : "rgba(46,127,241,0.92)",
    opacity: 1,
    boxShadow: isDarkMode ? "0 12px 22px rgba(46,127,241,0.18)" : "0 11px 20px rgba(46,127,241,0.16)",
  },
  "& .MuiButton-startIcon": {
    mr: 0.7,
  },
};

const textButtonStyles = {
  textTransform: "none",
  color: themeColors.color,
  opacity: 0.7,
  px: 0,
  "&:hover": {
    background: "transparent",
    opacity: 1,
  },
};

const footerStyles = {
  display: "flex",
  justifyContent: "flex-end",
  gap: "10px",
  mt: 2.8,
  pt: 1.5,
  borderTop: `1px solid ${isDarkMode ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.08)"}`,
};

export default TextMessagingSetupModal;
